import React from 'react';

import {getSearchableRegistrySupportedOligoAwareTypes} from 'constants/RegistryConstants';
import notifications from 'notifications';
import {
  isRequiredLinkFolderItemType,
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  RequiredLink,
} from 'schemas/fields/SchemaFieldConstants';
import {SCHEMA_INTERFACE_SUPPORTED_TYPES} from 'schemas/SchemaConstants';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {
  DropdownSearchType,
  Filter,
  FilterKey,
  keyToEmptyFilter,
  Operator,
  GLOBAL_SEARCH_URL,
  SearchParams,
  SearchType,
  SortKey,
} from 'search/SearchConstants';
import {tagSchemaToAllOligoAwareFolderItemTypes} from 'tag-schema/util/TagSchemaUtil';
import {NoResultsItem} from 'ui-library/attach-dropdown/AttachDropdown';
import SearchDropdown, {SearchResultInfo} from 'ui-library/attach-dropdown/SearchDropdown';
import {DEFAULT_DROPDOWN_SEARCH_LIMIT} from 'ui-library/dropdowns/SearchDropdownConstants';
import * as net from 'util/Net';
import {ElementKeyboardEvent} from 'util/Types';

interface Props {
  noResultsItems?: Array<NoResultsItem>;
  query: string;
  requiredLink: RequiredLink;
  selectAttachment: (id: string, name: string, url: string) => void;
}

export default class BioentityLinkTagSearchDropdown extends React.Component<Props> {
  dropdownRef = React.createRef<SearchDropdown>();

  _getFilters = (): Array<Filter> => {
    const {requiredLink} = this.props;
    const commonFilters = [keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES)];

    if (isRequiredLinkTagSchema(requiredLink)) {
      return [
        ...commonFilters,
        {key: FilterKey.SCHEMA, operator: Operator.IS_ONE_OF, value: [requiredLink.tagSchema.id]},
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: tagSchemaToAllOligoAwareFolderItemTypes(requiredLink.tagSchema),
        },
      ];
    } else if (isRequiredLinkFolderItemType(requiredLink)) {
      return [
        ...commonFilters,
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: [requiredLink.folderItemType],
        },
      ];
    } else if (isRequiredLinkSchemaInterface(requiredLink)) {
      return [
        ...commonFilters,
        {
          key: FilterKey.SCHEMA_INTERFACE,
          operator: Operator.IS_ONE_OF,
          value: [requiredLink.schemaInterface.id],
        },
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: SCHEMA_INTERFACE_SUPPORTED_TYPES,
        },
      ];
    } else {
      return [
        ...commonFilters,
        {
          key: FilterKey.TYPES,
          operator: Operator.IS_ONE_OF,
          value: getSearchableRegistrySupportedOligoAwareTypes(),
        },
      ];
    }
  };

  _doSearch = async (
    query: string,
    searchResultsCallback: (info: SearchResultInfo) => void
  ): Promise<void> => {
    const params: SearchParams = {
      query,
      filters: this._getFilters(),
      limit: DEFAULT_DROPDOWN_SEARCH_LIMIT,
      offset: 0,
      sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
    };

    try {
      const rv = (await GlobalSearchActions.doGlobalSearch(
        params,
        SearchType.GLOBAL,
        'metadata:ENTITY_LINK_SEARCH',
        false // requestConsistency
      )) as GlobalSearchResponse;
      const results = Object.keys(rv.fileSummariesById).map((id) => {
        const item = rv.fileSummariesById[id];
        return {
          item,
          item_id: item.id,
          name: item.name,
        };
      });
      searchResultsCallback({
        query,
        limit: params.limit,
        results,
        errorMessage: null,
        correlationId: rv.correlationId,
      });
    } catch (e) {
      if (net.isServerRequestError(e)) {
        const errorMessage = notifications.getFriendlyError(e) ?? 'An error occurred.';
        searchResultsCallback({query, limit: params.limit, results: [], errorMessage});
        return;
      }

      throw e;
    }
  };

  queryKeydownHandler = (e: ElementKeyboardEvent): boolean =>
    this.dropdownRef.current?.queryKeydownHandler(e) ?? false;

  override render(): JSX.Element {
    return (
      <SearchDropdown
        ref={this.dropdownRef}
        doSearch={this._doSearch}
        profilerTrackingParams={{
          url: GLOBAL_SEARCH_URL,
          searchType: DropdownSearchType.ENTITY_LINK,
        }}
        width="100%"
        {...this.props}
      />
    );
  }
}
